import React, {
  ButtonHTMLAttributes,
  FocusEvent,
  forwardRef,
  JSX,
  MouseEvent,
  ReactNode,
  Ref
} from 'react';
import { Button, Col, Row } from 'reactstrap';
import { FieldValues, FieldErrors, Mode } from 'react-hook-form';
import { AnyObjectSchema, AnySchema, Lazy } from 'yup';
import {
  CustomFormComponentsType,
  ExternalData,
  FieldWatcherProps,
  FormConfig,
  FormRef
} from '../types/types';
import Form from '../Form';
import RowCreator from './RowCreator';
import { createInitialValues, createValidationSchema } from './validations';

export interface Props<Data, DataType extends FieldValues> {
  formConfig: FormConfig;
  onSubmit?: (values: DataType) => void;
  externalData?: Data;
  onFormFieldsChange?: FieldWatcherProps<DataType>['onFormFieldsChange'];
  onFormBlur?: (e: FocusEvent<HTMLFormElement>) => void;
  initialValues?: DataType;
  validationSchema?: Lazy<AnySchema<unknown, unknown, unknown>, unknown> | AnyObjectSchema;
  formButtons?: ReactNode;
  yupContext?: Record<string, unknown>;
  children?: ReactNode | ReactNode[];
  submitButtonText?: string;
  submitButtonType?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  submitButtonClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  hideSubmitButton?: boolean;
  scrollToError?: boolean;
  mode?: Mode;
  reValidateMode?: 'onChange' | 'onBlur' | 'onSubmit' | undefined;
  errorCallback?: (errors: FieldErrors<DataType>) => void;
  CustomFormComponents?: CustomFormComponentsType;
  exposeSeparateFieldValue?: (fieldName: string, value: Record<string, unknown>) => void;
  id?: string;
  ariaLabelledBy?: string;
  legend?: string;
  withFieldSet?: boolean;
  withFormTag?: boolean;
}

function FormBuilder<Data extends ExternalData, DataType extends FieldValues>(
  props: Props<Data, DataType>,
  ref?: Ref<FormRef<DataType>>
): JSX.Element {
  const {
    formConfig,
    externalData,
    onSubmit,
    onFormFieldsChange,
    onFormBlur,
    initialValues,
    validationSchema,
    formButtons,
    children,
    submitButtonText,
    submitButtonType,
    submitButtonClick,
    hideSubmitButton,
    scrollToError,
    mode,
    reValidateMode,
    errorCallback,
    CustomFormComponents,
    exposeSeparateFieldValue,
    id,
    ariaLabelledBy,
    legend,
    withFieldSet,
    withFormTag
  } = props;

  const resolvedInitialValues = createInitialValues<DataType>(
    formConfig.rows,
    formConfig.keySuffix,
    initialValues
  );

  const resolvedValidationSchema =
    validationSchema || createValidationSchema(formConfig.rows, formConfig.keySuffix);

  return (
    <Form
      ref={ref}
      id={id}
      ariaLabelledBy={ariaLabelledBy}
      legend={legend}
      withFieldSet={withFieldSet}
      withFormTag={withFormTag}
      onSubmit={onSubmit}
      onFormBlur={onFormBlur}
      initialValues={resolvedInitialValues}
      onFormFieldsChange={onFormFieldsChange}
      validationSchema={resolvedValidationSchema}
      exposeSeparateFieldValue={exposeSeparateFieldValue}
      yupContext={externalData?.yupContext || {}}
      scrollToError={scrollToError}
      mode={mode}
      reValidateMode={reValidateMode}
      errorCallback={errorCallback}
    >
      {formConfig.rows.length > 0 &&
        formConfig.rows.map(row => (
          <RowCreator
            key={row.key}
            elements={row.elements}
            CustomFormComponents={CustomFormComponents}
            keySuffix={formConfig.keySuffix}
            externalData={externalData}
            // eslint-disable-next-line react/no-children-prop
            children={children}
          />
        ))}
      {formButtons ||
        (!hideSubmitButton ? (
          <Row className="form-buttons mt-4">
            <Col className="d-flex justify-content-end">
              <Button
                color="primary"
                type={submitButtonType || 'submit'}
                onClick={(e: MouseEvent<HTMLButtonElement>): void => {
                  if (submitButtonClick) {
                    e.preventDefault();
                    submitButtonClick(e);
                  }
                }}
              >
                {submitButtonText || 'Submit'}
              </Button>
            </Col>
          </Row>
        ) : null)}
    </Form>
  );
}

export default forwardRef(FormBuilder);
